"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Chat } from "@/components/chat";
import { DEFAULT_CHAT_MODEL } from "@/lib/ai/models";
import { generateUUID } from "@/lib/utils";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AuthFlow } from "@/components/auth/auth-flow";
import { ReferralBanner } from "@/components/referral-banner";
import { PromoCreditsBanner } from "@/components/promo-credits-banner";
import { storePendingChatLaunch } from "@/lib/pending-chat-launch";

// Utility function to get cookie value
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

export function LoggedOutHome() {
  const searchParams = useSearchParams();

  // Stabilize id, query and sourceNotebookId on first render - never recompute
  const [{ id, initialQuery, initialPrefill, sourceNotebookId, initialModelTier }] = useState(() => {
    const tier = searchParams.get("model_tier");
    const parsedModelTier: "basic" | "max" | undefined =
      tier === "max" || tier === "basic" ? tier : undefined;
    return {
      id: searchParams.get("id") || generateUUID(),
      initialQuery: searchParams.get("q"),
      initialPrefill: searchParams.get("prefill"),
      sourceNotebookId: searchParams.get("sourceNotebookId"),
      initialModelTier: parsedModelTier,
    };
  });

  const [isCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const saved = localStorage.getItem("sidebar:state");
    return saved !== "true";
  });
  const modelIdFromCookie = getCookie("chat-model") || DEFAULT_CHAT_MODEL;

  useEffect(() => {
    if (initialQuery) {
      storePendingChatLaunch(initialQuery);
    }
  }, [initialQuery]);

  const chatComponent = (
    <Chat
      key={id}
      id={id}
      initialMessages={[]}
      initialVisibilityType="private"
      isReadonly={false}
      autoResume={false}
      section="chat"
      initialUserQuery={undefined}
      initialPrefill={initialPrefill || initialQuery || undefined}
      sourceNotebookId={sourceNotebookId || undefined}
      initialModelTier={initialModelTier}
    />
  );

  return (
    <div className="flex flex-col h-dvh">
      <PromoCreditsBanner />
      <ReferralBanner />
      <SidebarProvider defaultOpen={!isCollapsed} className="flex-1 min-h-0">
        <SidebarInset>
          <AuthFlow>{chatComponent}</AuthFlow>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
